import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Users } from 'lucide-react';
import { leadService } from '../services/lead.service';
import { messageHandler } from '../services/message-handler.service';
import type { Lead, LeadFormData, LeadFilters as Filters } from '../types/lead';
import PageHeader from '../components/ui/PageHeader';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import LeadList from '../components/leads/LeadList';
import LeadForm from '../components/leads/LeadForm';
import LeadFilters from '../components/leads/LeadFilters';

type ModalType = 'create' | 'edit' | '';

interface ModalState {
  type: ModalType;
}

function Leads() {
  const [modal, setModal] = useState<ModalState>({ type: '' });
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<Filters>({});
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['leads', currentPage, filters],
    queryFn: () => leadService.getAll(currentPage, 20, filters),
  });

  const createMutation = useMutation({
    mutationFn: (data: LeadFormData) => leadService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      messageHandler.success('Lead criado com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: LeadFormData }) =>
      leadService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      messageHandler.success('Lead atualizado com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => leadService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      messageHandler.success('Lead excluído com sucesso!');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const updateContactMutation = useMutation({
    mutationFn: (id: number) => leadService.updateLastContact(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      messageHandler.success('Último contato atualizado com sucesso!');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const fullName = formData.get('full_name') as string;
    const email = formData.get('email') as string;
    const phone = formData.get('phone') as string;
    const phone2 = formData.get('phone2') as string;
    const phone3 = formData.get('phone3') as string;
    const representativeIdStr = formData.get('representative_id') as string;
    const origin = formData.get('origin') as string;
    const lastContactAt = formData.get('last_contact_at') as string;
    const integrationKeyStr = formData.get('integration_key') as string;
    const leadGroupIdsArray = formData.getAll('lead_group_ids') as string[];
    const type = formData.get('type') as string;

    const data: LeadFormData = {
      full_name: fullName,
      email: email || null,
      phone: phone || null,
      phone2: phone2 || null,
      phone3: phone3 || null,
      representative_id: representativeIdStr ? parseInt(representativeIdStr) : null,
      origin: origin || null,
      last_contact_at: lastContactAt || null,
      integration_key: integrationKeyStr ? parseInt(integrationKeyStr) : null,
      lead_group_ids:
        leadGroupIdsArray.length > 0 ? leadGroupIdsArray.map((id) => parseInt(id)) : null,
      type: type || 'other',
    };

    if (editingLead) {
      updateMutation.mutate({ id: editingLead.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = (lead: Lead) => {
    messageHandler.confirm(
      'Excluir Lead',
      'Tem certeza que deseja excluir este lead?',
      () => deleteMutation.mutate(lead.id)
    );
  };

  const handleUpdateContact = (lead: Lead) => {
    messageHandler.confirm(
      'Atualizar Último Contato',
      'Deseja atualizar a data do último contato para agora?',
      () => updateContactMutation.mutate(lead.id)
    );
  };

  const handleFilter = (newFilters: Filters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const closeModal = () => {
    setModal({ type: '' });
    setEditingLead(null);
  };

  return (
    <div className="py-6">
      <div className="px-4 sm:px-6 lg:px-8">
        <PageHeader
          title="Leads"
          description="Gerencie seus leads de marketing e acompanhe o histórico de contatos."
          icon={Users}
          actions={
            <Button
              onClick={() => {
                setEditingLead(null);
                setModal({ type: 'create' });
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Lead
            </Button>
          }
        />

        <LeadFilters onFilter={handleFilter} initialFilters={filters} />

        <LeadList
          leads={data?.data || []}
          onEdit={lead => {
            setEditingLead(lead);
            setModal({ type: 'edit' });
          }}
          onDelete={handleDelete}
          onUpdateContact={handleUpdateContact}
          isLoading={isLoading}
          pagination={data?.pagination}
          onPageChange={setCurrentPage}
        />

        <Modal
          isOpen={modal.type === 'create' || modal.type === 'edit'}
          onClose={closeModal}
          title={editingLead ? 'Editar Lead' : 'Novo Lead'}
        >
          <LeadForm
            lead={editingLead}
            onSubmit={handleSubmit}
            isLoading={createMutation.isPending || updateMutation.isPending}
            onClose={closeModal}
          />
        </Modal>
      </div>
    </div>
  );
}

export default Leads;
